import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, LayoutDashboard, FolderKanban, Package, FileCheck, Handshake, DollarSign, Calendar, LogOut, Receipt, FileText, Wallet, TrendingUp, Users, ChevronDown, ChevronRight, MessageSquare, CheckSquare, Banknote, UserCog, Calculator, Phone, Scale, PanelLeftClose, PanelLeft, Eye, HardHat, CalendarCheck, ArrowRightLeft, CreditCard, Coins, Activity, ClipboardList, Target, Key, LandPlot, CalendarDays, Settings } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Layout = ({ user, onLogout, children }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    operations: true,
    finance: true,
    administration: true,
    hr: true,
    legal: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const mainNavigation = [
    { name: t('nav.dashboard'), path: "/", icon: LayoutDashboard, testId: 'nav-dashboard', roles: ["owner", "management", "engineer", "contractor", "sales", "accountant", "hr", "attorney"] },
    { name: "Pulse Planner", path: "/pulse-planner", icon: CalendarDays, roles: ["owner", "management", "engineer", "contractor", "sales", "accountant", "hr", "attorney"] },
    { name: t('nav.projects'), path: "/projects", icon: FolderKanban, roles: ["owner", "management", "engineer"] },
    { name: t('nav.projectProgress'), path: "/project-progress", icon: TrendingUp, roles: ["owner", "management", "engineer"] },
    { name: t('nav.units'), path: "/units", icon: Package, roles: ["owner", "management", "sales", "contractor"] },
    { name: t('nav.teamTasks'), path: "/team-tasks", icon: MessageSquare, testId: 'nav-team-tasks', roles: ["owner", "management", "engineer", "contractor", "sales", "accountant", "hr", "attorney"] },
    { name: t('nav.myProgressLog'), path: "/my-progress-log", icon: CheckSquare, roles: ["contractor"] },
    { name: "Team Talk", path: "/messages", icon: MessageSquare, roles: ["contractor"] },
  ];

  const operationsNavigation = [
    { name: t('nav.offers'), path: "/offers", icon: FileCheck, roles: ["owner", "management"] },
    { name: t('nav.myOffers'), path: "/my-offers", icon: FileCheck, roles: ["contractor"] },
    { name: t('nav.engineerReports'), path: "/contractor-reports", icon: FileText, roles: ["contractor"] },
    { name: t('nav.myInvoices'), path: "/contractor-invoices", icon: Receipt, roles: ["contractor"] },
    { name: t('nav.partners'), path: "/partners", icon: Handshake, roles: ["owner", "management"] },
    { name: t('nav.subcontractors'), path: "/subcontractors", icon: HardHat, roles: ["owner", "management", "engineer"] },
    { name: t('engineerReports.title'), path: "/engineer-reports", icon: FileText, roles: ["engineer"] },
    { name: t('teamTalk.title'), path: "/messages", icon: MessageSquare, roles: ["owner", "management", "engineer", "accountant"] },
    { name: t('nav.reservations'), path: "/reservations", icon: CalendarCheck, roles: ["owner", "management", "sales"] },
    { name: t('nav.rentals'), path: "/rentals", icon: Key, roles: ["owner", "management", "sales"] },
    { name: t('clientInquiries.title'), path: "/client-inquiries", icon: Phone, roles: ["owner", "management", "sales"] },
  ];

  const financeNavigation = [
    { name: t('nav.costs'), path: "/costs", icon: CreditCard, roles: ["owner", "management"] },
    { name: t('nav.invoices'), path: "/invoices", icon: ClipboardList, roles: ["owner", "management", "accountant", "engineer"] },
    { name: t('nav.subcontractors'), path: "/subcontractors", icon: HardHat, roles: ["accountant"] },
    { name: t('loans.title'), path: "/loans", icon: Banknote, roles: ["owner", "management", "accountant"] },
    { name: t('finances.title'), path: "/finances", icon: DollarSign, testId: 'nav-finances', roles: ["owner", "management", "sales"] },
    { name: t('nav.potentialSales'), path: "/potential-sales", icon: Target, roles: ["owner", "management"] },
    { name: t('investments.title', 'Investment Opportunities'), path: "/investments", icon: LandPlot, roles: ["owner", "management"] },
    { name: t('assets.title'), path: "/assets", icon: Building2, testId: 'nav-assets', roles: ["owner", "management", "accountant"] },
    { name: t('projects.budget'), path: "/budget", icon: Wallet, roles: ["owner", "management"] },
    { name: t('nav.cashFlow'), path: "/cash-flow", icon: ArrowRightLeft, roles: ["owner", "management", "accountant"] },
  ];

  const administrationNavigation = [
    { name: t('users.title'), path: "/users", icon: Users, roles: ["owner"] },
    { name: t('nav.progressLog'), path: "/activity-logs", icon: Activity, roles: ["owner"] },
    { name: t('settings.title'), path: "/settings", icon: Settings, testId: 'nav-company-settings', roles: ["owner", "management"] },
    { name: t('settings.title'), path: "/my-settings", icon: Settings, testId: 'nav-personal-settings', roles: ["engineer", "contractor", "sales", "accountant", "hr", "attorney"] },
  ];

  const hrNavigation = [
    { name: t('employees.title'), path: "/employees", icon: UserCog, roles: ["hr", "owner", "management"] },
    { name: t('payroll.title'), path: "/payroll", icon: Calculator, roles: ["hr", "owner", "management", "accountant"] },
    { name: t('expenses.title'), path: "/other-expenses", icon: Coins, roles: ["hr", "owner", "management", "accountant"] },
  ];

  const legalNavigation = [
    { name: t('lawsuits.title'), path: "/lawsuits", icon: Scale, testId: 'nav-lawsuits', roles: ["attorney", "owner", "management"] },
  ];

  const filteredMainNav = mainNavigation.filter(item => item.roles.includes(user.role));
  const filteredOperationsNav = operationsNavigation.filter(item => item.roles.includes(user.role));
  const filteredFinanceNav = financeNavigation.filter(item => item.roles.includes(user.role));
  const filteredAdminNav = administrationNavigation.filter(item => item.roles.includes(user.role));
  const filteredHRNav = hrNavigation.filter(item => item.roles.includes(user.role));
  const filteredLegalNav = legalNavigation.filter(item => item.roles.includes(user.role));

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        data-testid={item.testId || `nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
        title={collapsed ? item.name : undefined}
        className={`relative flex items-center gap-3 px-3 py-2.5 mx-2 rounded-sm text-[13px] font-medium transition-all duration-150 group ${
          isActive
            ? "bg-white text-indigo-600 shadow-sm border border-slate-200"
            : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
        }`}
      >
        {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-indigo-600 rounded-r-full" />}
        <Icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
        {!collapsed && <span className="truncate">{item.name}</span>}
      </Link>
    );
  };

  const NavSection = ({ label, items, sectionKey }) => {
    if (items.length === 0) return null;
    const isExpanded = expandedSections[sectionKey];
    return (
      <div className="mt-6">
        <div
          data-testid={`nav-section-${sectionKey}`}
          className="px-4 py-1.5 cursor-pointer flex items-center justify-between group"
          onClick={() => !collapsed && toggleSection(sectionKey)}
        >
          {!collapsed && (
            <>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{label}</p>
              {isExpanded
                ? <ChevronDown className="w-3 h-3 text-slate-400 group-hover:text-slate-500 transition-colors" />
                : <ChevronRight className="w-3 h-3 text-slate-400 group-hover:text-slate-500 transition-colors" />
              }
            </>
          )}
          {collapsed && <div className="w-full border-t border-slate-200" />}
        </div>
        {(collapsed || isExpanded) && (
          <div className="mt-1.5 space-y-0.5">
            {items.map(item => <NavItem key={item.path} item={item} />)}
          </div>
        )}
      </div>
    );
  };

  const sidebarWidth = collapsed ? 'w-16' : 'w-64';
  const contentMargin = collapsed ? 'ml-0 md:ml-16' : 'ml-0 md:ml-64';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar — Light Industrial */}
      <div className={`hidden md:flex fixed inset-y-0 left-0 ${sidebarWidth} bg-slate-50 border-r border-slate-200 transition-all duration-300 ease-in-out z-30 flex-col`}>
        {/* Logo + Collapse */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 bg-white flex-shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-sm bg-indigo-600 flex items-center justify-center shadow-sm">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-base font-display font-semibold text-slate-900 tracking-tight uppercase">SitePulse</span>
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`p-2 rounded-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all duration-150 ${collapsed ? 'mx-auto' : ''}`}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden bg-slate-50">
          {/* Main */}
          <div>
            {!collapsed && (
              <div className="px-4 py-1.5">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{t('nav.main')}</p>
              </div>
            )}
            <div className="mt-1.5 space-y-0.5">
              {filteredMainNav.map(item => <NavItem key={item.path} item={item} />)}
            </div>
          </div>

          <NavSection label={t('nav.operations')} items={filteredOperationsNav} sectionKey="operations" />
          <NavSection label={t('nav.finance')} items={filteredFinanceNav} sectionKey="finance" />
          <NavSection label={t('nav.hr')} items={filteredHRNav} sectionKey="hr" />
          <NavSection label={t('nav.legal')} items={filteredLegalNav} sectionKey="legal" />
          <NavSection label={t('nav.administration')} items={filteredAdminNav} sectionKey="administration" />
        </nav>

        {/* Footer */}
        <div className="border-t border-slate-200 p-3 bg-white flex-shrink-0 space-y-2">
          {!collapsed && <LanguageSwitcher />}
          {!collapsed && (
            <div className="px-2 py-2 border-b border-slate-100 mb-2">
              <p className="text-[13px] font-semibold text-slate-900 truncate" data-testid="user-name">{user.name}</p>
              <p className="text-[11px] text-slate-500 truncate" data-testid="user-email">{user.email}</p>
            </div>
          )}
          {user.demoMode ? (
            <div className={`flex items-center gap-2 w-full px-3 py-2 rounded-sm text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 uppercase tracking-wider ${collapsed ? 'justify-center' : ''}`}>
              <Eye className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>View-only Demo</span>}
            </div>
          ) : (
            <button
              onClick={onLogout}
              data-testid="logout-button"
              title={collapsed ? t('common.signOut') : undefined}
              className={`flex items-center gap-2 w-full px-3 py-2 rounded-sm text-[12px] font-semibold text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all duration-150 uppercase tracking-wider ${collapsed ? 'justify-center' : ''}`}
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{t('common.signOut')}</span>}
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`${contentMargin} transition-all duration-300 ease-in-out min-h-screen`}>
        <header className="md:hidden bg-white border-b border-slate-200 p-4 space-y-3" data-testid="mobile-navigation">
          <div className="flex items-center justify-between gap-3"><span className="font-display font-semibold">SitePulse</span><div className="w-40"><LanguageSwitcher testId="mobile-language-switcher" /></div></div>
          <div className="flex items-center gap-3">
            <select aria-label={t('nav.main')} data-testid="mobile-page-select" value={location.pathname}
              onChange={e => { window.location.assign(e.target.value); }}
              className="min-w-0 flex-1 border border-slate-200 rounded-sm bg-white p-2 text-sm">
              {[...new Map([...filteredMainNav, ...filteredOperationsNav, ...filteredFinanceNav, ...filteredHRNav, ...filteredLegalNav, ...filteredAdminNav].map(item => [item.path, item])).values()].map(item =>
                <option key={item.path} value={item.path} data-testid={`mobile-nav-${item.path.replaceAll('/', '') || 'dashboard'}`}>{item.name}</option>)}
            </select>
            <button type="button" title={t('common.signOut')} aria-label={t('common.signOut')} data-testid="mobile-logout-button" onClick={onLogout} className="p-2 text-slate-500"><LogOut className="w-4 h-4" /></button>
          </div>
        </header>
        {/* Demo Banner */}
        {user.demoMode && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center justify-between" data-testid="demo-banner">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Demo Mode — Read-only preview</span>
            </div>
            <a href="/login" className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2 uppercase tracking-wider">Sign in for full access</a>
          </div>
        )}
        
        {/* Technical Grid Background Content */}
        <div className={`technical-grid min-h-screen p-4 md:p-8 ${user.demoMode ? 'demo-mode' : ''}`}>
          <div className="max-w-[1600px] min-w-0 mx-auto animate-fade-in">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
